import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiShoppingCart } from 'react-icons/fi';
import { productAPI } from '../api/services';
import ProductCard from '../components/ProductCard';
import { useStoreSettings } from '../context/StoreSettingsContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import './Home.css';

function PopularCategoriesCarousel({ items }) {
  const [idx, setIdx]         = useState(0);
  const [perView, setPerView] = useState(5);
  const timerRef              = useRef(null);
  const pausedRef             = useRef(false);
  const touchStartX           = useRef(0);

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w < 640) setPerView(3);
      else if (w < 900) setPerView(3);
      else if (w < 1200) setPerView(4);
      else setPerView(5);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  const max  = Math.max(0, items.length - perView);
  const prev = () => { setIdx(i => Math.max(0, i - 1)); pausedRef.current = true; };
  const next = () => { setIdx(i => i >= max ? 0 : i + 1); pausedRef.current = true; };
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 40) next();
    else if (diff < -40) prev();
  };

  useEffect(() => {
    if (items.length <= perView) return;
    timerRef.current = setInterval(() => {
      if (!pausedRef.current) setIdx(i => i >= max ? 0 : i + 1);
    }, 3000);
    return () => clearInterval(timerRef.current);
  }, [items.length, perView, max]);

  return (
    <section className="pop-cats-section">
      <div className="pop-cats-header">
        <span className="na-line" />
        <h2 className="pop-cats-title">Popular Categories</h2>
        <span className="na-line" />
      </div>
      <div className="pop-cats-carousel"
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
      >
        {items.length > perView && (
          <button className="cat-sec-arrow cat-sec-prev" onClick={prev}>&#8249;</button>
        )}
        <div className="pop-cats-viewport" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className="pop-cats-track" style={{ transform: `translateX(-${idx * (100 / perView)}%)` }}>
            {items.map((cat, i) => (
              <Link
                key={i}
                to={cat.category_id ? `/products?category_id=${cat.category_id}` : (cat.link || '/products')}
                className="pop-cat-card"
                style={{ width: `${100 / perView}%`, flexShrink: 0 }}
              >
                <div className="pop-cat-img-wrap">
                  <img src={cat.image_url || 'https://placehold.co/400x400?text=?'} alt={cat.name} />
                </div>
                <span className="pop-cat-name">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
        {items.length > perView && (
          <button className="cat-sec-arrow cat-sec-next" onClick={next}>&#8250;</button>
        )}
      </div>
    </section>
  );
}

function CategorySection({ categoryId, perViewOverride }) {
  const [products, setProducts] = useState([]);
  const [catName, setCatName]   = useState('');
  const [loading, setLoading]   = useState(true);
  const [idx, setIdx]           = useState(0);
  const [perView, setPerView]   = useState(perViewOverride || 6);
  const trackRef                = useRef(null);
  const timerRef                = useRef(null);
  const pausedRef               = useRef(false);
  const touchStartX             = useRef(0);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      productAPI.getProducts(categoryId, 0, 20),
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/categories`).then(r => r.json())
    ])
      .then(([prodRes, cats]) => {
        setProducts(prodRes.data || []);
        const cat = Array.isArray(cats) && cats.find(c => c.id === Number(categoryId));
        if (cat) setCatName(cat.name);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [categoryId]);

  // Calculate how many cards fit in viewport
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (perViewOverride) { setPerView(w < 640 ? 3 : perViewOverride); return; }
      if (w < 640) setPerView(3);
      else if (w < 900) setPerView(3);
      else if (w < 1200) setPerView(4);
      else setPerView(6);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [perViewOverride]);

  const max = Math.max(0, products.length - perView);
  const prev = () => { setIdx(i => Math.max(0, i - 1)); pausedRef.current = true; };
  const next = () => { setIdx(i => i >= max ? 0 : i + 1); pausedRef.current = true; };
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 40) next();
    else if (diff < -40) prev();
  };

  // Auto-scroll every 3 seconds
  useEffect(() => {
    if (products.length <= perView) return;
    timerRef.current = setInterval(() => {
      if (!pausedRef.current) {
        setIdx(i => i >= max ? 0 : i + 1);
      }
    }, 3000);
    return () => clearInterval(timerRef.current);
  }, [products.length, perView, max]);

  if (loading || !products.length) return null;

  const cardWidth = trackRef.current
    ? trackRef.current.offsetWidth / perView
    : 240;

  return (
    <section className="cat-sec">
      <div className="cat-sec-header">
        <span className="na-line" />
        <h2 className="cat-sec-title">{catName || 'Category'}</h2>
        <span className="na-line" />
      </div>

      <div className="cat-sec-wrapper">
        {products.length > perView && (
          <button className="cat-sec-arrow cat-sec-prev" onClick={prev}>&#8249;</button>
        )}
      <div
        className="cat-sec-viewport"
        ref={trackRef}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="cat-sec-track"
          style={{ transform: `translateX(-${idx * (100 / perView)}%)` }}
        >
          {products.map(p => {
            const hasDisc = !!p.discount_price;
            const isInactive = p.is_active === false;
            const oos = p.stock === 0 || isInactive;
            const discPct = hasDisc ? Math.round(((p.price - p.discount_price) / p.price) * 100) : 0;
            return (
              <Link
                to={`/products/${p.id}`}
                key={p.id}
                className="cat-sec-card"
                style={{ width: `${100 / perView}%`, flexShrink: 0 }}
              >
                <div className="cat-sec-img-wrap">
                  {oos && <div className="na-oos-badge">Out of Stock</div>}
                  {!oos && hasDisc && <div className="cat-sec-disc-badge">-{discPct}%</div>}
                  <img src={p.image_url || 'https://placehold.co/400x400?text=?'} alt={p.name} />
                </div>
                <div className="cat-sec-info">
                  <p className="cat-sec-name">{p.name}</p>
                  {!isInactive && (
                    <div className="cat-sec-prices">
                      <span className="cat-sec-price">Rs.{(p.discount_price || p.price).toFixed(0)}</span>
                      {hasDisc && <span className="cat-sec-orig">Rs.{p.price.toFixed(0)}</span>}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      {products.length > perView && (
        <button className="cat-sec-arrow cat-sec-next" onClick={next}>&#8250;</button>
      )}
      </div>

      <div className="na-view-all-row">
        <Link to={`/products?category_id=${categoryId}`} className="na-view-all-btn">View All</Link>
      </div>
    </section>
  );
}

function SpecialSection({ title, products, viewAllLink, perViewOverride, blink }) {
  const [idx, setIdx]         = useState(0);
  const [perView, setPerView] = useState(perViewOverride || 6);
  const timerRef              = useRef(null);
  const pausedRef             = useRef(false);
  const touchStartX           = useRef(0);

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (perViewOverride) { setPerView(w < 640 ? 3 : perViewOverride); return; }
      if (w < 640) setPerView(3);
      else if (w < 900) setPerView(3);
      else if (w < 1200) setPerView(4);
      else setPerView(6);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [perViewOverride]);

  const max  = Math.max(0, products.length - perView);
  const prev = () => { setIdx(i => Math.max(0, i - 1)); pausedRef.current = true; };
  const next = () => { setIdx(i => i >= max ? 0 : i + 1); pausedRef.current = true; };
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 40) next();
    else if (diff < -40) prev();
  };

  useEffect(() => {
    if (products.length <= perView) return;
    timerRef.current = setInterval(() => {
      if (!pausedRef.current) setIdx(i => i >= max ? 0 : i + 1);
    }, 3500);
    return () => clearInterval(timerRef.current);
  }, [products.length, perView, max]);

  if (!products.length) return null;

  return (
    <section className="cat-sec">
      <div className="cat-sec-header">
        <span className="na-line" />
        <h2 className={`cat-sec-title${blink ? ' cat-sec-title-blink' : ''}`}>{title}</h2>
        <span className="na-line" />
      </div>
      <div className="cat-sec-wrapper">
        {products.length > perView && (
          <button className="cat-sec-arrow cat-sec-prev" onClick={prev}>&#8249;</button>
        )}
        <div className="cat-sec-viewport"
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="cat-sec-track" style={{ transform: `translateX(-${idx * (100 / perView)}%)` }}>
            {products.map(p => {
              const hasDisc = !!p.discount_price;
              const isInactive = p.is_active === false;
              const oos = p.stock === 0 || isInactive;
              const discPct = hasDisc ? Math.round(((p.price - p.discount_price) / p.price) * 100) : 0;
              return (
                <Link key={p.id} to={`/products/${p.id}`} className="cat-sec-card" style={{ width: `${100 / perView}%`, flexShrink: 0 }}>
                  <div className="cat-sec-img-wrap">
                    {oos && <div className="na-oos-badge">Out of Stock</div>}
                    {!oos && hasDisc && <div className="cat-sec-disc-badge">-{discPct}%</div>}
                    <img src={p.image_url || 'https://placehold.co/400x400?text=?'} alt={p.name} />
                  </div>
                  <div className="cat-sec-info">
                    <p className="cat-sec-name">{p.name}</p>
                    {!isInactive && (
                      <div className="cat-sec-prices">
                        <span className="cat-sec-price">Rs.{(p.discount_price || p.price).toFixed(0)}</span>
                        {hasDisc && <span className="cat-sec-orig">Rs.{p.price.toFixed(0)}</span>}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
        {products.length > perView && (
          <button className="cat-sec-arrow cat-sec-next" onClick={next}>&#8250;</button>
        )}
      </div>
      <div className="na-view-all-row">
        <Link to={viewAllLink} className="na-view-all-btn">View All</Link>
      </div>
    </section>
  );
}

function StickyBannerWithNextSection({ imageUrl, btnLink, children }) {
  const bannerRef  = useRef(null);
  const nextRef    = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    // Set sticky top to sit just below the sticky header
    const updateTop = () => {
      const header = document.querySelector('.header-wrapper');
      if (header && bannerRef.current) {
        bannerRef.current.style.top = header.offsetHeight + 'px';
      }
    };
    updateTop();
    window.addEventListener('resize', updateTop);

    const onScroll = () => {
      if (!bannerRef.current || !nextRef.current || !overlayRef.current) return;
      const bannerH = bannerRef.current.offsetHeight;
      const nextTop = nextRef.current.getBoundingClientRect().top;
      const headerH = parseInt(bannerRef.current.style.top || '0', 10);
      // overlap starts when next section reaches banner bottom, ends when it reaches banner top
      const overlap = Math.max(0, Math.min(1, 1 - (nextTop - headerH) / bannerH));
      overlayRef.current.style.opacity = (overlap * 0.75).toFixed(3);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', updateTop);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const Tag = btnLink ? 'a' : 'div';
  const tagProps = btnLink ? { href: btnLink } : {};

  return (
    <div>
      <div ref={bannerRef} style={{ position: 'sticky', top: 0, zIndex: 1 }}>
        <Tag {...tagProps} style={{ display: 'block', position: 'relative' }}>
          <img
            src={imageUrl}
            alt="Banner"
            className="mid-banner-img"
            style={{ width: '100%', objectFit: 'cover', display: 'block' }}
          />
          <div
            ref={overlayRef}
            style={{ position: 'absolute', inset: 0, background: '#000', opacity: 0, pointerEvents: 'none' }}
          />
        </Tag>
      </div>
      {children && (
        <div ref={nextRef} className="overlay-mode" style={{ position: 'relative', zIndex: 2 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function sendEdit(section) {
  if (window.parent !== window) window.parent.postMessage({ type: 'SECTION_CLICKED', section }, '*');
}

function EditChip({ label }) {
  return <span className="edit-chip">✏ {label}</span>;
}



function Home() {
  const { settings: ctxSettings, editMode } = useStoreSettings();
  const [settings, setSettings]         = useState(ctxSettings);
  const { hero, categories: catCfg, featuredCats = [], carouselCats = [], midBanner = {} } = settings;
  const [featured, setFeatured]         = useState([]);
  const [trending, setTrending]         = useState([]);
  const [staffFavs, setStaffFavs]       = useState([]);
  const [newArrivalsP,  setNewArrivalsP]  = useState([]);
  const [dealsP,        setDealsP]        = useState([]);
  const [mostSellingP,  setMostSellingP]  = useState([]);
  const [loading, setLoading]           = useState(true);
  const [trendingIdx, setTrendingIdx]   = useState(0);
  const [trendingStep, setTrendingStep] = useState(340);
  const [trendingPerView, setTrendingPerView] = useState(4);
  const [catDotIdx,   setCatDotIdx]     = useState(0);
  const catPaused = useRef(false);
  const midBannerOverlayRef = useRef(null);
  const [banners, setBanners]           = useState([]);
  const [bannerIdx, setBannerIdx]       = useState(0);
  const [bannersLoading, setBannersLoading] = useState(true);

  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    fetch(`${API}/homepage`)
      .then(r => r.json())
      .then(d => {
        if (d.settings) setSettings(d.settings);
        setFeatured(d.featured     || []);
        setTrending(d.trending     || []);
        setStaffFavs(d.staffFavs   || []);
        setNewArrivalsP(d.newArrivals   || []);
        setDealsP(d.deals             || []);
        setMostSellingP(d.mostSelling  || []);
        setBanners(d.banners       || []);
        setBannersLoading(false);
      })
      .catch(() => setBannersLoading(false))
      .finally(() => setLoading(false));
  }, []);

  // Auto-advance banner every 5 seconds
  useEffect(() => {
    if (banners.length < 2) return;
    const id = setInterval(() => setBannerIdx(i => (i + 1) % banners.length), 5000);
    return () => clearInterval(id);
  }, [banners.length]);

  // Darken mid-banner as trending section scrolls over it
  useEffect(() => {
    const handleScroll = () => {
      const overlay = midBannerOverlayRef.current;
      const trending = trendingRef.current;
      if (!overlay || !trending) return;
      const trendingTop = trending.getBoundingClientRect().top;
      const bannerHeight = 700;
      const progress = Math.min(1, Math.max(0, (bannerHeight - trendingTop) / bannerHeight));
      overlay.style.opacity = progress * 0.7;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-advance trending carousel every 3 seconds
  useEffect(() => {
    if (trending.length <= 4) return;
    const id = setInterval(() => {
      setTrendingIdx(i => (i + 1) % (trending.length - 3));
    }, 3000);
    return () => clearInterval(id);
  }, [trending.length]);


  // Auto-advance category carousel every 3s, pause on hover
  useEffect(() => {
    if (carouselCats.length <= 4) return;
    const id = setInterval(() => {
      if (catPaused.current) return;
      const el = catRowRef.current;
      setCatDotIdx(prev => {
        const maxIdx = Math.max(0, carouselCats.length - 4);
        const next = prev >= maxIdx ? 0 : prev + 1;
        if (el) {
          const itemWidth = el.offsetWidth / 4;
          el.scrollTo({ left: next * itemWidth, behavior: 'smooth' });
        }
        return next;
      });
    }, 3000);
    return () => clearInterval(id);
  }, [carouselCats.length]);

  const goPrev = () => setBannerIdx(i => (i - 1 + banners.length) % banners.length);
  const goNext = () => setBannerIdx(i => (i + 1) % banners.length);

  const catHeaderRef   = useScrollReveal({ threshold: 0.3 });
  const catRowRef      = useScrollReveal({ threshold: 0.15 });
  const trendingRef    = useScrollReveal({ threshold: 0.08 });
  const featHeaderRef  = useScrollReveal({ threshold: 0.3 });
  const featGridRef    = useScrollReveal({ threshold: 0.05 });
  const newsletterRef  = useScrollReveal({ threshold: 0.35 });
  const sfGridRef      = useScrollReveal({ threshold: 0.08 });
  const naGridRef      = useScrollReveal({ threshold: 0.08 });
  const naHeaderRef    = useScrollReveal({ threshold: 0.3 });

  // Trending carousel: responsive card step + per-view count
  useEffect(() => {
    const update = () => {
      if (window.innerWidth <= 768) {
        // CSS: wrapper 16px each side, gap 10px → card = (vw-42)/2, step = card+gap
        const step = Math.floor((window.innerWidth - 42) / 2) + 10;
        setTrendingStep(step);
        setTrendingPerView(2);
      } else {
        setTrendingStep(340);
        setTrendingPerView(4);
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const catItemsPerView = () => window.innerWidth <= 768 ? 3 : 4;

  const scrollToItem = (i) => {
    const el = catRowRef.current;
    if (!el) return;
    const perView = catItemsPerView();
    const itemWidth = el.offsetWidth / perView;
    const maxIdx = Math.max(0, carouselCats.length - perView);
    const clamped = Math.max(0, Math.min(maxIdx, i));
    el.scrollTo({ left: clamped * itemWidth, behavior: 'smooth' });
    setCatDotIdx(clamped);
  };

  // Drag + touch + snap for category row
  useEffect(() => {
    const el = catRowRef.current;
    if (!el) return;
    let isDown = false, startX, scrollLeft;

    const snapToNearest = () => {
      const perView = catItemsPerView();
      const itemWidth = el.offsetWidth / perView;
      const maxIdx = Math.round((el.scrollWidth - el.offsetWidth) / itemWidth);
      const nearest = Math.max(0, Math.min(maxIdx, Math.round(el.scrollLeft / itemWidth)));
      el.scrollTo({ left: nearest * itemWidth, behavior: 'smooth' });
      setCatDotIdx(nearest);
    };

    const onMouseDown  = (e) => { isDown = true; startX = e.pageX - el.offsetLeft; scrollLeft = el.scrollLeft; el.classList.add('dragging'); };
    const onMouseLeave = () => { if (isDown) { isDown = false; el.classList.remove('dragging'); snapToNearest(); } };
    const onMouseUp    = () => { if (isDown) { isDown = false; el.classList.remove('dragging'); snapToNearest(); } };
    const onMouseMove  = (e) => { if (!isDown) return; e.preventDefault(); el.scrollLeft = scrollLeft - (e.pageX - el.offsetLeft - startX) * 1.4; };

    let touchStartX, touchScrollLeft;
    const onTouchStart = (e) => { touchStartX = e.touches[0].pageX; touchScrollLeft = el.scrollLeft; };
    const onTouchMove  = (e) => { el.scrollLeft = touchScrollLeft + (touchStartX - e.touches[0].pageX); };
    const onTouchEnd   = () => snapToNearest();

    el.addEventListener('mousedown',  onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup',    onMouseUp);
    el.addEventListener('mousemove',  onMouseMove);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: true });
    el.addEventListener('touchend',   onTouchEnd);
    return () => {
      el.removeEventListener('mousedown',  onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup',    onMouseUp);
      el.removeEventListener('mousemove',  onMouseMove);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  }, []);

  return (
    <div>
      {/* ── Hero Banner Slider ───────────────────────────────────────── */}
      <section
        className={`hero-slider${editMode ? ' editable-section' : ''}`}
        onClick={editMode ? () => sendEdit('banners') : undefined}
      >
        {editMode && <EditChip label="Edit Banners" />}

        {bannersLoading ? null : banners.length > 0 ? (
          <>
            {/* Slides — stacked, active fades in */}
            {banners.map((b, i) => (
              <div
                key={b.id}
                className={`slider-slide${i === bannerIdx ? ' active' : ''}`}
                style={{
                  backgroundImage: b.image_url ? `url(${b.image_url})` : 'none',
                  backgroundColor: b.bg_color || '#1e1b4b',
                }}
              >
                {(b.title || b.subtitle || b.btn_text) && (
                  <div className="slide-content" style={{ color: b.text_color || '#fff' }}>
                    {b.title    && <h2 className="slide-title">{b.title}</h2>}
                    {b.subtitle && <p  className="slide-sub">{b.subtitle}</p>}
                    {b.btn_text && (
                      <Link to={b.btn_link || '/products'} className="slide-btn">
                        {b.btn_text}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Featured product cards overlaid on banner */}
            {featured.length > 0 && (
              <div className="banner-products">
                {featured.slice(0, 3).map(p => (
                  <Link key={p.id} to={`/products/${p.id}`} className="banner-product-card">
                    <img
                      src={p.image_url || 'https://placehold.co/80x80?text=?'}
                      alt={p.name}
                      className="banner-product-img"
                    />
                    <div className="banner-product-info">
                      <span className="banner-product-name">{p.name}</span>
                      <span className="banner-product-price">
                        Rs {(p.discount_price || p.price).toFixed(0)}/-
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Arrows */}
            {banners.length > 1 && (
              <>
                <button className="slider-arrow slider-prev" onClick={goPrev} aria-label="Previous">&#8249;</button>
                <button className="slider-arrow slider-next" onClick={goNext} aria-label="Next">&#8250;</button>
              </>
            )}

            {/* Dots */}
            {banners.length > 1 && (
              <div className="slider-dots">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    className={`slider-dot${i === bannerIdx ? ' active' : ''}`}
                    onClick={() => setBannerIdx(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          /* Placeholder only shown after load confirms no banners */
          <div className="slider-empty">
            <div className="slider-empty-icon">🖼️</div>
            <h3>No banners added yet</h3>
            <p>Go to Admin → Store → Banners to add homepage banner images</p>
            <Link to="/products" className="btn btn-accent">Browse Products</Link>
          </div>
        )}
      </section>




      {/* ── Popular Categories ───────────────────────── */}
      {(settings.popularCategories || []).length > 0 && (
        <PopularCategoriesCarousel items={settings.popularCategories || []} />
      )}

      {/* ── Category Strip ───────────────────────────── */}
      {(settings.categoryStrip || []).length > 0 && (
        <div className="cat-strip">
          {(settings.categoryStrip || []).map((cat, i) => (
            <Link
              key={i}
              to={cat.category_id ? `/products?category_id=${cat.category_id}` : (cat.link || '/products')}
              className="cat-strip-item"
            >
              {cat.image_url && (
                <div className="cat-strip-img-wrap">
                  <img src={cat.image_url} alt={cat.name} className="cat-strip-img" />
                </div>
              )}
              <span className="cat-strip-name">{cat.name}</span>
            </Link>
          ))}
        </div>
      )}

      {/* ── Homepage Category Sections ───────────────── */}
      {(() => {
        const cats = settings.homepageCategories || [];
        const renderSec = (sec, extraProps = {}) => {
          if (sec.type === 'new_arrivals') return <SpecialSection key="new_arrivals" title="New Arrivals" products={newArrivalsP} viewAllLink="/new-arrivals" blink {...extraProps} />;
          if (sec.type === 'day_sale')     return <SpecialSection key="day_sale"     title="Day Sale"     products={dealsP}       viewAllLink="/deals" blink {...extraProps} />;
          if (sec.type === 'most_selling') return <SpecialSection key="most_selling" title="Most Selling" products={mostSellingP} viewAllLink="/most-selling" {...extraProps} />;
          if (sec.category_id)            return <CategorySection key={sec.category_id} categoryId={sec.category_id} {...extraProps} />;
          return null;
        };
        const OVERLAY_PROPS = { perViewOverride: 5 };
        const result = [];
        for (let i = 0; i < cats.length; i++) {
          const sec = cats[i];
          if (sec.type === 'mid_banner') {
            const mb = settings.midBanner || {};
            if (!mb.image_url) continue;
            const next = cats[i + 1] || null;
            result.push(
              <StickyBannerWithNextSection key="mid_banner" imageUrl={mb.image_url} btnLink={mb.btn_link}>
                {next ? renderSec(next, OVERLAY_PROPS) : null}
              </StickyBannerWithNextSection>
            );
            if (next) i++;
            continue;
          }
          const el = renderSec(sec);
          if (el) result.push(el);
        }
        return result;
      })()}
    </div>
  );
}

export default Home;
